import React, { useState } from "react"
import Select, { Props } from "react-select"
import { SelectOption } from "@interfaces/ui"
import { CSSObject } from "@emotion/serialize"
import { useField } from "formik"
import clsx from "clsx"

export interface FormikSelectProps extends Props {
  error?: string
  defaultOption?: SelectOption
  controlStyle?: CSSObject
  selectClassName?: string
  setField: (field: string, value: any, shouldValidate?: boolean) => void
  valueType?: string
  name: string
}

export const FormikSelect: React.FC<FormikSelectProps> = ({
  className,
  selectClassName,
  controlStyle,
  name,
  setField,
  valueType,
  value,
  ...props
}) => {
  const [selectValue, setSelectValue] = useState<SelectOption>()

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [field, meta] = useField(name)
  const errorText = meta.error

  const handleChange = (value: SelectOption | null) => {
    if (!value) {
      return
    }

    switch (true) {
      case valueType === "label":
        setField(`${name}`, value.label)
        setSelectValue(value)
        break
      case valueType === "isProvince":
        setField("partner_province", value.label)
        setField("partner_city", "")
        setSelectValue(value)
        break
      case value.value === "":
        // @ts-ignore
        setSelectValue(null)
        break
      default:
        setField(`${name}`, value.value)
        setSelectValue(value)
        break
    }
  }

  return (
    <div className={clsx("relative", className)}>
      <Select
        {...props}
        key={`my_unique_select_key__${JSON.stringify(selectValue)}`}
        onChange={(value1) => handleChange(value1)}
        value={value ?? selectValue}
        className={selectClassName}
        styles={{
          control: (props) => ({
            ...props,
            border: errorText ? "1px solid #ef2547" : "1px solid #231F20",
            borderColor: errorText ? "#ef2547" : "#231F20",
            boxShadow: errorText ? "0 0 0 #ef2547" : "0 0 0 #231F20",
            borderRadius: "5px",
            minHeight: 0,
            padding: "10px 18px",
            "&:hover": {
              border: "1px solid #231F20",
              boxShadow: "0 0 0 #231F20",
            },
            "&:focus": {
              border: "1px solid #231F20",
              boxShadow: "0 0 0 #231F20",
            },
            "@media(max-width: 576px)": {
              fontSize: "12px",
            },
            ...controlStyle,
          }),
          input: (props) => ({
            ...props,
            margin: 0,
            paddingTop: 0,
            paddingBottom: 0,
          }),
          placeholder: (props) => ({
            ...props,
            color: "#979797",
            marginLeft: 0,
            marginRight: 0,
          }),
          dropdownIndicator: (props) => ({
            ...props,
            padding: 0,
          }),
          valueContainer: (props) => ({
            ...props,
            padding: "0 5px 0 0",
          }),
          option: (props, { isSelected }) => ({
            ...props,
            backgroundColor: isSelected ? "#0DAD8E" : "white",
            "&:active": {
              backgroundColor: "rgba(13, 173, 142, 0.5)",
            },
            "@media(max-width: 576px)": {
              fontSize: "12px",
            },
          }),
        }}
      />
      {errorText && <small className="text-red">{errorText}</small>}
    </div>
  )
}
